import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  ButtonToolbar,
  Button,
  Col,
  Glyphicon,
  Grid,
  Row,
} from "react-bootstrap";
import toastr from "toastr";
import Swal from "sweetalert2";

import {
  ROLENAMES,
  VOUCHER_KEY,
  DEFINE_VOUCHER_KEY,
  EXPORT_VOUCHER_KEY,
  OVERRIDE_VOUCHER_KEY,
} from "provider/PermissionProvider/permissions";
import usePermission from "provider/PermissionProvider";
import {
  defineVoucher,
  deleteVoucher,
  exportVoucher,
  getVouchers,
  updateVoucher,
  getPackagesByFamilyId,
  getProductNumbersByFamilyPackageName,
} from "./action";
import { VouchersActivityLogsPath } from "routes/paths";
import { RemoteGrid } from "../../Grid";
import { Panel, PanelHeader } from "../../Panel";
import Loader from "../../Loader";
import { Filters, Filter } from "../../Filter";
import { famOption } from "../../Filter/Filters";
import { VoucherFormModal } from "./VoucherFormModal";
import ExportContainer from "./ExportFormContainer";
import {
  UsageLimitType,
  VouchersListColumns,
  VouchersFilters,
  Order,
  FormMode,
} from "../../../constant";

const initialVoucher = {
  family: "",
  packageName: "",
  productNumber: "",

  id: "",
  duration: "",
  usageLimit: "",
  usageLimitType: UsageLimitType.PowerTimeInSeconds.displayName,
  numberOfUnlock: 1,
  requiresLogin: true,
  requiresOnline: false,
};

const filters = {
  productNumber: { text: "", value: -1 },
  packageName: { text: "", value: -1 },
};

const defaultOption = { id: "-1", name: "-" };

const Vouchers = (props) => {
  const { permissionContext } = usePermission();
  const { hasPermission } = permissionContext.permissions;
  const [isInitializing, setIsInitializing] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const [order, setOrder] = useState({
    sortBy: VouchersListColumns.product_number,
    orderBy: Order.asc,
  });
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState(0);
  const [operation, setOperation] = useState(0);
  const [isGridLoading, setIsGridLoading] = useState(true);
  // form
  const [voucher, setVoucher] = useState(initialVoucher);
  const [formMode, setFormMode] = useState(FormMode.Add);
  // modal
  const [showVoucherFormModal, setShowVoucherFormModal] = useState(false);
  const [showExportFormModal, setShowExportFormModal] = useState(false);
  // filter
  const [selectedFamily, setSelectedFamily] = useState({ text: "", value: -1 });
  const [selectedPackageName, setSelectedPackageName] = useState({
    text: "",
    value: -1,
  });
  const [selectedProductNumber, setSelectedProductNumber] = useState({
    text: "",
    value: -1,
  });
  const [packages, setPackages] = useState([{ id: "", name: "" }]);
  const [packagesKey, setPackagesKey] = useState(
    `packages${new Date().getTime()}`
  );
  const [productNumbers, setProductNumbers] = useState([{ id: "", name: "" }]);
  const [productNumberKey, setProductNumberKey] = useState(
    `productNumber${new Date().getTime()}`
  );
  const [hasOverridePermission, setHasOverridePermission] = useState(false);

  useEffect(() => {
    props.getVouchers().then(() => {
      setIsGridLoading(false);
    });

    setIsInitializing(false);
  }, []);

  useEffect(() => {
    searchGrid();
  }, [search]);

  useEffect(() => {
    // set override permission based on selected unit family
    setHasOverridePermission(hasPermission(selectedFamily.value, VOUCHER_KEY, OVERRIDE_VOUCHER_KEY))
  }, [selectedFamily.value])

  const handleDefineVoucher = () => {
    setFormMode(FormMode.Add);
    setVoucher({
      ...initialVoucher,
      usageLimitType: hasOverridePermission? UsageLimitType.PowerTimeInSeconds.value : UsageLimitType.PowerTimeInSeconds.displayName,
      family: selectedFamily.text,
      packageName: selectedPackageName.text,
      productNumber: selectedProductNumber.text,
    });
    toggleVoucherFormModal();
  };

  const handleUpdateVoucher = (voucher) => {
    // set override permission based on selected voucher's unit family
    let tempHasOverridePermission = false;
    if(Array.isArray(voucher?.units) && voucher.units.length > 0) {
      tempHasOverridePermission = hasPermission(voucher.units[0].family_id, VOUCHER_KEY, OVERRIDE_VOUCHER_KEY)
    }
    setHasOverridePermission(tempHasOverridePermission);

    // if user has override permission, show usage limit type dropdown and use value (number)
    // otherwise, show usage limit type textfield and use display name (string)
    let usageLimitTypeDisplay = voucher.usage_limit_type;
    if (!tempHasOverridePermission) {
      const usageLimitType = Object.keys(UsageLimitType).find(key => UsageLimitType[key].value === voucher.usage_limit_type);
      usageLimitTypeDisplay = usageLimitType ? UsageLimitType[usageLimitType].displayName : UsageLimitType.PowerTimeInSeconds.displayName;
    }

    setFormMode(FormMode.Edit);
    setVoucher({
      id: voucher.id,
      family: voucher.units.length > 0 ? voucher.units[0].family_name : "",
      packageName: voucher.package_name,
      productNumber: voucher.product_number,
      duration: voucher.duration,
      usageLimit: voucher.usage_limit,
      usageLimitType: usageLimitTypeDisplay,
      numberOfUnlock: voucher.number_of_unlock,
      requiresLogin: voucher.requires_login,
      requiresOnline: voucher.requires_online,
    });
    toggleVoucherFormModal();
  };

  const handleDeleteVoucher = (voucher) => {
    Swal.fire({
      title: "Delete voucher?",
      text: `Do you really want to delete voucher for '${voucher.product_number}'? This process cannot be undone.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No",
      reverseButtons: true,
      focusConfirm: false,
      focusCancel: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        return await props.deleteVoucher(voucher.id);
      },
    }).then(async (response) => {
      if (response.value) {
        if (response.value.isSuccess) {
          Swal.fire("Deleted!", response.value.payload.message, "success");
          await props.getVouchers(
            pagination.currentPage,
            pagination.pageSize,
            order.sortBy,
            order.orderBy,
            search,
            selectedFamily.value,
            selectedPackageName.text,
            selectedProductNumber.text,
            searchBy,
            operation
          );
        } else {
          Swal.fire(
            "An error has occurred",
            response.value.payload && response.value.payload.error
              ? response.value.payload.error
              : "An unexpected error has occurred. Please try again later.",
            "error"
          );
        }
      }
    });
  };

  const handleExportVoucher = (voucher) => {
    setVoucher({
      id: voucher.id,
      duration: voucher.duration,
    });
    toggleExportFormModal();
  };

  const handleSubmit = async (values, actions) => {
    if (values.id) {
      const response = await props.updateVoucher(values.id, values, hasOverridePermission);
      if (response.isSuccess) {
        toggleVoucherFormModal();
        toastr.info(response.payload.message, "Update");

        await props.getVouchers(
          pagination.currentPage,
          pagination.pageSize,
          order.sortBy,
          order.orderBy,
          search,
          selectedFamily.value,
          selectedPackageName.text,
          selectedProductNumber.text,
          searchBy,
          operation
        );
      } else {
        toastr.error(response.payload.error, "Error");
      }
    } else {
      const response = await props.defineVoucher(values, hasOverridePermission);
      if (response.isSuccess) {
        toggleVoucherFormModal();
        toastr.info(`New voucher has been added.`, "Add");

        await props.getVouchers(
          pagination.currentPage,
          pagination.pageSize,
          order.sortBy,
          order.orderBy,
          search,
          selectedFamily.value,
          selectedPackageName.text,
          selectedProductNumber.text,
          searchBy,
          operation
        );
      } else {
        toastr.error(response.payload.error, "Error");
      }
    }

    actions.setSubmitting(false);
  };

  const handleFilterValueChange = async (state) => {
    let packagesParam = state.packageName;
    let productNumberParam = state.productNumber;

    if (selectedFamily.value !== state.family.value) {
      props.getPackagesByFamilyId(state.family.value).then((packages) => {
        setPackages([defaultOption, ...(Array.isArray(packages) ? packages : [])]);
        setPackagesKey(`packages${new Date().getTime()}`);
        setProductNumbers([]);
      });
      packagesParam = { text: "", value: -1 };
      productNumberParam = { text: "", value: -1 };
    } else if (selectedPackageName.text !== state.packageName.text) {
      props
        .getProductNumbersByFamilyPackageName(
          state.family.value,
          state.packageName.text
        )
        .then((productNumbers) => {
          const refinedProductNumbers =
            productNumbers.isSuccess && productNumbers.value
              ? productNumbers.value.map((productNumber) => {
                  return {
                    id: productNumber.id,
                    name: productNumber.product_number,
                  };
                })
              : [];
          setProductNumbers([defaultOption, ...refinedProductNumbers]);
          setProductNumberKey(`productNumber${new Date().getTime()}`);
        });
      productNumberParam = { text: "", value: -1 };
    }

    setSelectedFamily(state.family);
    setSelectedPackageName(packagesParam);
    setSelectedProductNumber(productNumberParam);
    setIsGridLoading(true);
    setPagination({
      ...pagination,
      currentPage: 1,
    });

    await props.getVouchers(
      pagination.currentPage,
      pagination.pageSize,
      order.sortBy,
      order.orderBy,
      search,
      state.family.value,
      packagesParam.text,
      productNumberParam.text,
      state.searchBy,
      state.operation
    );

    setIsGridLoading(false);
  };

  const handleTableChange = async (type, state) => {
    setIsGridLoading(true);
    setPagination({
      currentPage: state.page,
      pageSize: state.sizePerPage,
    });
    setOrder({
      sortBy: VouchersListColumns[state.sortField],
      orderBy: Order[state.sortOrder],
    });

    await props.getVouchers(
      state.page,
      state.sizePerPage,
      VouchersListColumns[state.sortField],
      Order[state.sortOrder],
      search,
      selectedFamily.value,
      selectedPackageName.text,
      selectedProductNumber.text,
      searchBy,
      operation
    );

    setIsGridLoading(false);
  };

  const searchGrid = async () => {
    setIsGridLoading(true);

    await props.getVouchers(
      pagination.currentPage,
      pagination.pageSize,
      order.sortBy,
      order.orderBy,
      search,
      selectedFamily.value,
      selectedPackageName.text,
      selectedProductNumber.text,
      searchBy,
      operation
    );

    setIsGridLoading(false);
  };

  const handleSearch = async (searchCriteria) => {
    setSearch(searchCriteria.searchText);
    setSearchBy(searchCriteria.searchBy);
    setOperation(searchCriteria.operation);
    setPagination({
      ...pagination,
      currentPage: 1,
    });
  };

  const toggleVoucherFormModal = () => {
    setShowVoucherFormModal(!showVoucherFormModal);
  };

  const toggleExportFormModal = () => {
    setShowExportFormModal(!showExportFormModal);
  };

  const enableDefineVoucher = () => {
    return (
      selectedFamily.value !== -1 &&
      selectedPackageName.value !== -1 &&
      selectedProductNumber.value !== -1 &&
      hasPermission(selectedFamily.value, VOUCHER_KEY, DEFINE_VOUCHER_KEY)
    );
  };

  const columns = [
    {
      dataField: "id",
      text: "Id",
      hidden: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "product_number",
      text: "Product Number",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "units[0].family_name",
      text: "Family",
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "package_name",
      text: "Package Name",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "duration",
      text: "Duration",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "usage_limit",
      text: "Usage Limit",
      sort: true,
      style: { wordWrap: "break-word" },
      headerStyle: { wordWrap: "break-word" },
      title: (cell) => cell,
    },
    {
      dataField: "buttons",
      text: "",
      formatter: (cell, row) => (
        <div className="btn-toolbar">
          <Button
            disabled={
              !hasPermission(
                row.units[0].family_id,
                VOUCHER_KEY,
                EXPORT_VOUCHER_KEY
              )
            }
            bsStyle="primary"
            title="Export Voucher"
            onClick={() => handleExportVoucher(row)}
          >
            <Glyphicon glyph="export" />
          </Button>
          <Button
            disabled={
              !hasPermission(
                row.units[0].family_id,
                VOUCHER_KEY,
                DEFINE_VOUCHER_KEY
              )
            }
            bsStyle="primary"
            title="Edit Voucher"
            onClick={() => handleUpdateVoucher(row)}
          >
            <Glyphicon glyph="pencil" />
          </Button>
          <Button
            disabled={
              !hasPermission(
                row.units[0].family_id,
                VOUCHER_KEY,
                DEFINE_VOUCHER_KEY
              )
            }
            bsStyle="danger"
            title="Delete Voucher"
            onClick={() => handleDeleteVoucher(row)}
          >
            <Glyphicon glyph="trash" />
          </Button>
        </div>
      ),
    },
  ];

  if (isInitializing) {
    return <Loader fullpage message="Loading vouchers" />;
  }

  return (
    <Grid fluid>
      <h2>Vouchers</h2>
      <Row>
        <Col md={10} mdPush={2}>
          <Panel>
            <PanelHeader>
              <ButtonToolbar>
                {
                  <Button
                    bsStyle="success"
                    bsSize="small"
                    onClick={handleDefineVoucher}
                    disabled={!enableDefineVoucher()}
                  >
                    <Glyphicon glyph="plus" /> Define Voucher
                  </Button>
                }
                <Button
                  bsSize="small"
                  bsStyle="primary"
                  onClick={() => props.history.push(VouchersActivityLogsPath)}
                >
                  <Glyphicon glyph="tasks" /> View Activity Logs
                </Button>
              </ButtonToolbar>
            </PanelHeader>
            <div className="panel-body">
              <RemoteGrid
                filterFields={VouchersFilters}
                data={props.vouchers}
                columns={columns}
                pagination={pagination}
                loading={isGridLoading}
                onTableChange={handleTableChange}
                onSearch={handleSearch}
              />
            </div>
          </Panel>
        </Col>
        <Filters
          className="col-md-2 col-md-pull-10"
          initialValues={filters}
          hasFamily
          onFilterValueChange={handleFilterValueChange}
          familyOption={famOption.FAMILY_PACKAGES}
          roleNameId={ROLENAMES[VOUCHER_KEY].id}
        >
          <Filter
            key={packagesKey}
            name="packageName"
            title="Package Name"
            options={packages}
          />
          <Filter
            key={productNumberKey}
            name="productNumber"
            title="Product Number"
            options={productNumbers}
          />
        </Filters>
      </Row>
      <VoucherFormModal
        show={showVoucherFormModal}
        formMode={formMode}
        initialValues={voucher}
        hasOverridePermission={hasOverridePermission}
        onFormSubmit={handleSubmit}
        onModalClose={toggleVoucherFormModal}
      />
      <ExportContainer
        selectedVoucher={voucher}
        show={showExportFormModal}
        onModalClose={toggleExportFormModal}
      />
    </Grid>
  );
};

function mapStateToProps(state) {
  return {
    vouchers: state.voucher.vouchers,
    currentUser: state.account.currentUser,
  };
}

const mapDispatchToProps = {
  getVouchers,
  defineVoucher,
  exportVoucher,
  updateVoucher,
  deleteVoucher,
  getPackagesByFamilyId,
  getProductNumbersByFamilyPackageName,
};

export default connect(mapStateToProps, mapDispatchToProps)(Vouchers);
